<script setup>

// Imports
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { getSwiper } from '@/utils/swiper-loader'

// Props
const props = defineProps({
  block: {
    type: Object,
    default: () => ({})
  }
})

const items = computed(() => (
  Array.isArray(props.block?.industryShowcaseMatrix)
    ? props.block.industryShowcaseMatrix
    : []
))
const hasMultipleSlides = computed(() => items.value.length > 1)

const sectionRef = ref(null)
const imageSwiperRef = ref(null)
const paginationRef = ref(null)
const activeSlideIndex = ref(0)
const contentIndex = ref(0)
const isCopyVisible = ref(true)
const isSwiperInitialized = ref(false)
const isSectionVisible = ref(false)
const prefersReducedMotion = ref(false)

let swiperInstance = null
let initPromise = null
let observerInstance = null
let pendingContentIndex = 0
let copyFadeTimer = null
let copyFadeFrame = 0

const AUTOPLAY_DELAY = 7500
const CONTENT_FADE_MS = 300

const carouselStatusText = computed(() => {
  if (!items.value.length) return ''
  return `Slide ${activeSlideIndex.value + 1} of ${items.value.length}`
})

const cancelCopyFade = () => {
  if (copyFadeTimer) {
    clearTimeout(copyFadeTimer)
    copyFadeTimer = null
  }
  if (copyFadeFrame) {
    cancelAnimationFrame(copyFadeFrame)
    copyFadeFrame = 0
  }
}

const revealCopy = () => {
  copyFadeTimer = null
  contentIndex.value = pendingContentIndex
  copyFadeFrame = requestAnimationFrame(() => {
    copyFadeFrame = requestAnimationFrame(() => {
      isCopyVisible.value = true
      copyFadeFrame = 0
    })
  })
}

const syncCopy = (index) => {
  activeSlideIndex.value = index
  pendingContentIndex = index

  if (prefersReducedMotion.value) {
    cancelCopyFade()
    contentIndex.value = index
    isCopyVisible.value = true
    return
  }

  if (index === contentIndex.value && isCopyVisible.value) return

  isCopyVisible.value = false
  cancelCopyFade()
  copyFadeTimer = setTimeout(revealCopy, CONTENT_FADE_MS)
}

const initSwiper = async () => {
  if (!imageSwiperRef.value || isSwiperInitialized.value || !hasMultipleSlides.value) return
  if (initPromise) return initPromise

  initPromise = (async () => {
    const { Swiper, modules } = await getSwiper(['Autoplay', 'Pagination'])
    if (!imageSwiperRef.value || isSwiperInitialized.value || !hasMultipleSlides.value) return

    swiperInstance = new Swiper(imageSwiperRef.value, {
      loop: true,
      speed: 650,
      slidesPerView: 1,
      autoplay: prefersReducedMotion.value
        ? false
        : {
            delay: AUTOPLAY_DELAY,
            disableOnInteraction: false
          },
      pagination: paginationRef.value
        ? {
            el: paginationRef.value,
            clickable: true,
            renderBullet: (index, className) =>
              `<button type="button" class="${className}" aria-label="Go to slide ${index + 1} of ${items.value.length}"><span class="industry-showcase-carousel__pagination-fill" aria-hidden="true"></span></button>`
          }
        : undefined,
      modules,
      on: {
        slideChange: (swiper) => {
          syncCopy(swiper.realIndex)
        }
      }
    })

    syncCopy(swiperInstance.realIndex)
    isSwiperInitialized.value = true
  })()

  return initPromise
}

const revealSection = () => {
  isSectionVisible.value = true
  observerInstance?.disconnect()
  observerInstance = null
  return initSwiper()
}

const goToPrev = async () => {
  if (!isSwiperInitialized.value) await revealSection()
  swiperInstance?.slidePrev()
}

const goToNext = async () => {
  if (!isSwiperInitialized.value) await revealSection()
  swiperInstance?.slideNext()
}

onMounted(() => {
  if (typeof window !== 'undefined' && 'matchMedia' in window) {
    prefersReducedMotion.value = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  }

  nextTick(() => {
    if (!sectionRef.value) return

    if (typeof IntersectionObserver === 'undefined') {
      revealSection()
      return
    }

    observerInstance = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          revealSection()
        }
      })
    }, {
      threshold: 0.1
    })

    observerInstance.observe(sectionRef.value)
  })
})

onBeforeUnmount(() => {
  cancelCopyFade()
  observerInstance?.disconnect()
  observerInstance = null
  swiperInstance?.destroy?.(true, true)
  swiperInstance = null
})

</script>

<!-- -------------------------------------------------------------------------------------------------
    Template
------------------------------------------------------------------------------------------------- -->

<template>
<section ref="sectionRef"
  class="industry-showcase-carousel-section section-mb"
  :class="{ 'is-reduced-motion': prefersReducedMotion }"
  role="region"
  aria-label="Industry showcase carousel">
  <span class="sr-only" aria-live="polite">{{ carouselStatusText }}</span>
  <div class="container">
    <div v-if="block.headingAdvanced"
      class="industry-showcase-carousel__head">
      <div v-if="block.headingAdvanced"
        v-html="block.headingAdvanced.html"
        class="industry-showcase-carousel__heading"></div>
    </div>

    <div v-if="items.length"
      class="industry-showcase-carousel__item">

      <div class="industry-showcase-carousel__item-content">
        <div class="industry-showcase-carousel__item-content-head">
          <div class="industry-showcase-carousel__item-content-heading-stack"
            :class="{ 'is-copy-hidden': !isCopyVisible }">
            <div v-for="(item, index) in items"
              :key="`${item.id}-heading`"
              class="industry-showcase-carousel__item-content-heading"
              :class="{ 'is-active': index === contentIndex }">
              <div v-if="item.iconField?.length"
                class="industry-showcase-carousel__item-content-heading-icon">
                <img :src="item.iconField[0].url"
                  :alt="item.iconField[0].title"
                  loading="lazy"
                />
              </div>
              <span v-if="item.title">
                {{ item.title }}
              </span>
            </div>
          </div>

          <div v-if="hasMultipleSlides"
            class="industry-showcase-carousel__item-navigation">
            <button type="button"
              class="industry-showcase-carousel__item-navigation-button --prev"
              aria-label="Previous slide"
              @click="goToPrev">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="15" viewBox="0 0 20 15" fill="none" aria-hidden="true" focusable="false">
                <path d="M10.3496 4.00543e-05L4.60658 6.51082H19.2456V7.92354L4.60658 7.92354L10.3496 14.4343H8.84473L-0.000108719 7.21718L8.84473 4.00543e-05H10.3496Z" fill="black"/>
              </svg>
            </button>
            <button type="button"
              class="industry-showcase-carousel__item-navigation-button --next"
              aria-label="Next slide"
              @click="goToNext">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="15" viewBox="0 0 20 15" fill="none" aria-hidden="true" focusable="false">
                <path d="M8.89602 14.4343L14.639 7.9235H0V6.51079L14.639 6.51079L8.89602 0H10.4009L19.2457 7.21714L10.4009 14.4343H8.89602Z" fill="black"/>
              </svg>
            </button>
          </div>
        </div>

        <div class="industry-showcase-carousel__item-footer-stack"
          :class="{ 'is-copy-hidden': !isCopyVisible }">
          <div v-for="(item, index) in items"
            :key="`${item.id}-footer`"
            class="industry-showcase-carousel__item-footer"
            :class="{ 'is-active': index === contentIndex }">
            <div v-if="item.heading"
              v-html="item.heading.html"
              class="industry-showcase-carousel__item-title"></div>
            <div v-if="item.description"
              v-html="item.description.html"
              class="industry-showcase-carousel__item-description"></div>

            <GlobalsButton
              v-if="item.linkTitle?.title && item.linkTitle?.url"
              :title="item.linkTitle.title"
              :url="item.linkTitle.url"
              :class="item.linkTitle.class"
              :type="item?.buttonSettings?.linkType"
              :size="item?.buttonSettings?.linkSize"
              :icon="item?.buttonSettings?.linkIcon"
              class="industry-showcase-carousel__item-button"
            />
          </div>
        </div>
      </div>

      <div class="industry-showcase-carousel__item-image-wrapper">
        <div ref="imageSwiperRef"
          class="industry-showcase-carousel__item-image swiper">
          <div class="swiper-wrapper">
            <div v-for="item in items"
              :key="item.id"
              class="swiper-slide">
              <GlobalsImage
                :landscape="item.imageDesktop"
                :portrait="item.imageMobile"
                :maxWidth="674"
                :quality="100"
                :loading="isSectionVisible ? 'eager' : 'lazy'"
              />
            </div>
          </div>
        </div>

        <div v-show="hasMultipleSlides"
          ref="paginationRef"
          class="industry-showcase-carousel__item-pagination"
          :style="`--progress-duration: ${AUTOPLAY_DELAY}ms`"></div>
      </div>
    </div>
  </div>
</section>
</template>

<!-- -------------------------------------------------------------------------------------------------
    Styling
------------------------------------------------------------------------------------------------- -->

<style scoped>

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.industry-showcase-carousel__head {
  display: flex;
  flex-direction: column;
  margin-bottom: 32px;

  @media (min-width: 1024px) {
    align-items: center;
    text-align: center;
    margin-bottom: 48px;
  }
}

.industry-showcase-carousel__item {
  display: flex;
  flex-direction: column;
  background-color: var(--color-gray-100);
  border-radius: 16px;
  overflow: hidden;

  @media (min-width: 1024px) {
    flex-direction: row;
  }
}

.industry-showcase-carousel__item-content {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  padding: 20px 24px 32px;

  @media (min-width: 1024px) {
    justify-content: space-between;
  }

  @media (min-width: 1440px) {
    padding: 64px;
  }
}

.industry-showcase-carousel__item-content-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-bottom: 42px;
}

.industry-showcase-carousel__item-content-heading-stack,
.industry-showcase-carousel__item-footer-stack {
  display: grid;
  min-width: 0;
  transform: translateZ(0);
  transition: opacity 0.3s ease;
}

.industry-showcase-carousel__item-content-heading-stack.is-copy-hidden,
.industry-showcase-carousel__item-footer-stack.is-copy-hidden {
  opacity: 0;
  pointer-events: none;
}

.industry-showcase-carousel__item-content-heading-stack > *,
.industry-showcase-carousel__item-footer-stack > * {
  grid-area: 1 / 1;
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
}

.industry-showcase-carousel__item-content-heading-stack > *.is-active,
.industry-showcase-carousel__item-footer-stack > *.is-active {
  opacity: 1;
  visibility: visible;
  pointer-events: auto;
}

.industry-showcase-carousel-section.is-reduced-motion .industry-showcase-carousel__item-content-heading-stack,
.industry-showcase-carousel-section.is-reduced-motion .industry-showcase-carousel__item-footer-stack {
  transition: none;
}

@media (prefers-reduced-motion: reduce) {
  .industry-showcase-carousel__item-content-heading-stack,
  .industry-showcase-carousel__item-footer-stack {
    transition: none;
  }
}

.industry-showcase-carousel__item-content-heading {
  display: flex;
  align-items: center;
  gap: 8px;

  font-size: 18px;
  font-weight: 500;
  line-height: 132%; /* 23.76px */
  letter-spacing: -0.36px;
  color: var(--color-gray-800);

  @media (min-width: 1024px) {
    gap: 16px;

    font-size: 22px;
    line-height: 144%; /* 31.68px */
    letter-spacing: -0.44px;
  }
}

.industry-showcase-carousel__item-content-heading-icon img {
  width: 32px;
  height: 32px;

  @media (min-width: 1024px) {
    width: 52px;
    height: 52px;
  }
}

.industry-showcase-carousel__item-navigation {
  flex-shrink: 0;
  display: flex;
  gap: 14px;

  @media (min-width: 1024px) {
    gap: 16px;
  }
}

.industry-showcase-carousel__item-navigation-button {
  width: 36px;
  height: 36px;
  background-color: var(--color-white);
  border: 1px solid var(--color-gray-150);
  border-radius: 5.5px;

  display: flex;
  align-items: center;
  justify-content: center;

  transition: all 0.3s ease-in-out;

  &:hover {
    background-color: rgba(255, 255, 255, 0.9);
    border-color: var(--color-black);
  }

  &:focus-visible {
    outline: 2px solid var(--color-black);
    outline-offset: 2px;
  }

  @media (min-width: 1024px) {
    width: 48px;
    height: 48px;
    border-radius: 8px;
  }
}

.industry-showcase-carousel__item-navigation-button svg {
  width: 14.434px;
  height: 10.826px;

  @media (min-width: 1024px) {
    width: 19.246px;
    height: 14.434px;
  }
}

.industry-showcase-carousel__item-footer {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.industry-showcase-carousel__item-title {
  font-size: 20px;
  font-weight: 500;
  line-height: 128%; /* 25.6px */
  letter-spacing: -0.4px;
  color: var(--color-gray-700);

  @media (min-width: 1024px) {
    font-size: 28px;
    font-weight: 500;
    line-height: 37px;
    letter-spacing: -0.56px;
    color: var(--color-gray-900);
  }
}

.industry-showcase-carousel__item-description {
  margin-top: 8px;
  min-height: 56px;

  font-size: 16px;
  font-weight: 400;
  line-height: 136%; /* 21.76px */
  letter-spacing: -0.32px;
  color: var(--color-gray-700);

  @media (min-width: 1024px) {
    margin-top: 20px;
    min-height: initial;

    font-size: 20px;
    line-height: 27px;
    letter-spacing: -0.4px;
    color: var(--color-gray-800);
  }
}

.industry-showcase-carousel__item-button {
  margin-top: 16px;

  @media (min-width: 1024px) {
    margin-top: 32px;
  }

  @media (min-width: 1440px) {
    margin-top: 60px;
  }
}

.industry-showcase-carousel__item-image-wrapper {
  flex-shrink: 0;
  width: 100%;
  position: relative;
  overflow: hidden;

  @media (min-width: 1024px) {
    width: calc(674 / 1552 * 100%);
  }
}

.industry-showcase-carousel__item-image {
  width: 100%;
  height: 100%;
  overflow: hidden;

  @media (min-width: 1440px) {
    height: 616px;
  }
}

.industry-showcase-carousel__item-image .swiper-wrapper {
  display: flex;

  @media (min-width: 1024px) {
    height: 100%;
  }
}

.industry-showcase-carousel__item-image .swiper-slide {
  flex-shrink: 0;
  width: 100%;
}

.industry-showcase-carousel__item-pagination {
  --swiper-pagination-color: transparent;
  --swiper-pagination-bullet-inactive-color: transparent;
  --swiper-pagination-bullet-opacity: 1;
  --swiper-pagination-bullet-inactive-opacity: 1;

  position: absolute;
  left: 31px;
  right: 26px;
  bottom: 28px;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: auto;

  @media (min-width: 1024px) {
    left: 47px;
    right: 47px;
    bottom: 43px;
  }
}

.industry-showcase-carousel__item-pagination :deep(.swiper-pagination-bullet) {
  flex: 1 1 0;
  width: auto;
  min-width: 0;
  height: 3px;
  margin: 0;
  padding: 0;
  border: 0;
  border-radius: 0;
  position: relative;
  overflow: hidden;
  background: rgb(255 255 255 / 0.5);
  opacity: 1;
  cursor: pointer;
}

.industry-showcase-carousel__item-pagination :deep(.swiper-pagination-bullet:hover) {
  background: rgb(255 255 255 / 0.85);
}

.industry-showcase-carousel__item-pagination :deep(.swiper-pagination-bullet-active) {
  cursor: default;
  background: rgb(255 255 255 / 0.5);
}

.industry-showcase-carousel__item-pagination :deep(.industry-showcase-carousel__pagination-fill) {
  position: absolute;
  inset: 0;
  background: var(--color-white);
  transform: scaleX(0);
  transform-origin: left center;
  pointer-events: none;
}

.industry-showcase-carousel__item-pagination :deep(.swiper-pagination-bullet-active .industry-showcase-carousel__pagination-fill) {
  animation: industry-showcase-carousel-progress var(--progress-duration, 7.5s) linear forwards;
}

.industry-showcase-carousel-section.is-reduced-motion :deep(.swiper-pagination-bullet-active .industry-showcase-carousel__pagination-fill) {
  animation: none;
  transform: scaleX(1);
}

@media (prefers-reduced-motion: reduce) {
  .industry-showcase-carousel__item-pagination :deep(.swiper-pagination-bullet-active .industry-showcase-carousel__pagination-fill) {
    animation: none;
    transform: scaleX(1);
  }
}

@keyframes industry-showcase-carousel-progress {
  from {
    transform: scaleX(0);
  }

  to {
    transform: scaleX(1);
  }
}

</style>
